import {
  demoFieldValue,
  demoItemListPreview,
  demoProductListPreview,
  generateIpsum,
  KIND,
} from '../ipsum/catalogIpsum';
import {
  evaluateMockExpression,
  isMockExpression,
  parseFieldTagAttributes,
  resolveMockDefault,
} from '../ipsum/catalogMock';

export {
  generateIpsum,
  KIND,
  demoFieldValue,
  demoProductListPreview,
  demoItemListPreview,
  evaluateMockExpression,
  isMockExpression,
  resolveMockDefault,
};

const BLOCK_ID_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;
const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const FIELD_TYPES = new Set([
  'text',
  'textarea',
  'boolean',
  'number',
  'color',
  'media',
  'product_list',
  'item_list',
]);

const blockdefOpenPattern = /^blockdef\s+"([^"]+)"(.*)$/i;
const fieldTagPattern = /^field\s+"([^"]+)"(.*)$/i;
const blockRefPattern = /\{\{\s*block\s+"([^"]+)"\s*\}\}/gi;

export const hasBlockdefs = (html) => (html || '').toLowerCase().includes('{{ blockdef ');

export const DEFAULT_LIST_DEMO_COUNT = 3;

export const isListFieldType = (type) => type === 'item_list' || type === 'product_list';

export const resolveListDemoCount = (field) => {
  let count = DEFAULT_LIST_DEMO_COUNT;
  const raw = field?.demo_count ?? field?.demoCount;
  if (raw !== undefined && raw !== null && raw !== '') {
    const n = parseInt(String(raw), 10);
    if (Number.isFinite(n) && n >= 0) count = n;
  }
  const max = resolveListMaxItems(field);
  if (max > 0 && count > max) return max;
  return count;
};

export const resolveListMinItems = (field) => {
  const raw = field?.min_items ?? field?.minItems;
  const n = parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export const resolveListMaxItems = (field) => {
  const raw = field?.max_items ?? field?.maxItems;
  const n = parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const readQuotedAttrValue = (src) => {
  if (!src.startsWith('"')) {
    throw new Error('expected quoted attribute value');
  }
  let value = '';
  let i = 1;
  while (i < src.length) {
    const ch = src[i];
    if (ch === '\\' && i + 1 < src.length) {
      value += src[i + 1];
      i += 2;
      continue;
    }
    if (ch === '"') {
      return { value, consumed: i + 1 };
    }
    value += ch;
    i += 1;
  }
  throw new Error('unclosed attribute value');
};

const parseTagAttributes = (rest) => {
  const out = {};
  let cursor = (rest || '').trim();
  while (cursor) {
    const eq = cursor.indexOf('=');
    if (eq <= 0) break;
    const key = cursor.slice(0, eq).trim().toLowerCase();
    cursor = cursor.slice(eq + 1).trim();
    if (!cursor.startsWith('"')) break;
    const { value, consumed } = readQuotedAttrValue(cursor);
    out[key] = value;
    cursor = cursor.slice(consumed).trim();
  }
  return out;
};

const filterBlocksByReferences = (blocks, compositionHTML) => {
  const refs = new Set();
  blockRefPattern.lastIndex = 0;
  let m = blockRefPattern.exec(compositionHTML || '');
  while (m) {
    refs.add(m[1].trim());
    m = blockRefPattern.exec(compositionHTML || '');
  }
  blockRefPattern.lastIndex = 0;
  if (refs.size === 0) {
    return [];
  }
  return (blocks || []).filter((b) => refs.has(b.id));
};

const skipTemplateWhitespace = (html, pos) => {
  let i = pos;
  while (i < html.length && /\s/.test(html[i])) {
    i += 1;
  }
  return i;
};

const collectLeadingBlockdefIds = (html) => {
  const ids = new Set();
  let pos = 0;
  const src = html || '';
  for (;;) {
    pos = skipTemplateWhitespace(src, pos);
    if (pos >= src.length || !src.slice(pos).toLowerCase().startsWith('{{ blockdef ')) {
      break;
    }
    try {
      const { tag, end: openEnd } = parseTemplateTagContent(src, pos);
      const { id } = parseBlockdefOpen(tag);
      ids.add(id);
      const { closeEnd } = extractBlockdefBody(src, openEnd);
      pos = closeEnd;
    } catch {
      break;
    }
  }
  return ids;
};

const filterBlocksExcludingUnusedPreamble = (sourceHTML, blocks) => {
  const preamble = collectLeadingBlockdefIds(sourceHTML);
  const refs = new Set();
  blockRefPattern.lastIndex = 0;
  let m = blockRefPattern.exec(sourceHTML || '');
  while (m) {
    refs.add(m[1].trim());
    m = blockRefPattern.exec(sourceHTML || '');
  }
  blockRefPattern.lastIndex = 0;
  const filtered = (blocks || []).filter((b) => {
    if (preamble.has(b.id) && !refs.has(b.id)) {
      return false;
    }
    return true;
  });
  if (filtered.length === 0 && (blocks || []).length > 0 && preamble.size === blocks.length) {
    return blocks;
  }
  return filtered;
};

const filterCompositionKeepOnlyBlocks = (compositionHTML, blocks) => {
  const allowed = new Set((blocks || []).map((b) => b.id));
  const src = compositionHTML || '';
  let out = '';
  let pos = 0;
  blockRefPattern.lastIndex = 0;
  let match = blockRefPattern.exec(src);
  while (match) {
    const absStart = match.index;
    const absEnd = match.index + match[0].length;
    const id = match[1].trim();
    out += src.slice(pos, absStart);
    if (allowed.has(id)) {
      out += match[0];
    }
    pos = absEnd;
    match = blockRefPattern.exec(src);
  }
  blockRefPattern.lastIndex = 0;
  return (out + src.slice(pos)).trim();
};

const hasBlockReferenceForId = (compositionHTML, blockId) => {
  const id = String(blockId || '').trim();
  if (!id) return false;
  blockRefPattern.lastIndex = 0;
  let m = blockRefPattern.exec(compositionHTML || '');
  while (m) {
    if (m[1].trim() === id) return true;
    m = blockRefPattern.exec(compositionHTML || '');
  }
  blockRefPattern.lastIndex = 0;
  return false;
};

const dedupeBlockReferencesKeepLast = (compositionHTML) => {
  const src = compositionHTML || '';
  const matches = [];
  blockRefPattern.lastIndex = 0;
  let m = blockRefPattern.exec(src);
  while (m) {
    matches.push({ start: m.index, end: m.index + m[0].length, id: m[1].trim() });
    m = blockRefPattern.exec(src);
  }
  blockRefPattern.lastIndex = 0;
  if (matches.length <= 1) return src;
  const lastIndex = new Map();
  matches.forEach((item, index) => lastIndex.set(item.id, index));
  let out = '';
  let pos = 0;
  matches.forEach((item, index) => {
    if (lastIndex.get(item.id) !== index) {
      out += src.slice(pos, item.start);
      pos = item.end;
      return;
    }
    out += src.slice(pos, item.end);
    pos = item.end;
  });
  return out + src.slice(pos);
};

const ensureBlockReferences = (compositionHTML, blocks) => {
  if (!blocks?.length) return compositionHTML || '';
  const missing = blocks.filter((b) => !hasBlockReferenceForId(compositionHTML, b.id));
  if (missing.length === 0) return compositionHTML || '';
  const refs = missing.map((b) => `{{ block "${b.id}" }}`).join('');
  const lower = (compositionHTML || '').toLowerCase();
  const closeMain = lower.lastIndexOf('</main>');
  if (closeMain >= 0) {
    return `${compositionHTML.slice(0, closeMain)}\n${refs}\n${compositionHTML.slice(closeMain)}`;
  }
  if (!(compositionHTML || '').trim()) {
    return `<main class="catalog-page">\n${refs}\n</main>`;
  }
  return `${compositionHTML}\n${refs}`;
};

const parseTemplateTagContent = (src, start) => {
  if (start + 2 > src.length || src.slice(start, start + 2) !== '{{') {
    throw new Error(`invalid template tag at ${start}`);
  }
  const closeIdx = src.indexOf('}}', start + 2);
  if (closeIdx < 0) {
    throw new Error(`unclosed template tag at ${start}`);
  }
  const tag = src.slice(start + 2, closeIdx).trim();
  return { tag, end: closeIdx + 2 };
};

const findNextStructuralTag = (src) => {
  const lower = src.toLowerCase();
  const needles = ['{{ blockdef ', '{{ endblockdef }}', '{{ field '];
  let best = -1;
  needles.forEach((needle) => {
    const idx = lower.indexOf(needle);
    if (idx >= 0 && (best < 0 || idx < best)) best = idx;
  });
  return best;
};

const extractBlockdefBody = (fullSrc, openTagEnd) => {
  let depth = 1;
  let pos = openTagEnd;
  while (pos < fullSrc.length) {
    const rel = findNextStructuralTag(fullSrc.slice(pos));
    if (rel < 0) throw new Error('blockdef missing endblockdef');
    const abs = pos + rel;
    const { tag, end: tagEnd } = parseTemplateTagContent(fullSrc, abs);
    const lower = tag.toLowerCase();
    if (lower.startsWith('blockdef ')) {
      depth += 1;
      pos = tagEnd;
      continue;
    }
    if (lower === 'endblockdef') {
      depth -= 1;
      if (depth === 0) {
        return { body: fullSrc.slice(openTagEnd, abs), closeEnd: tagEnd };
      }
      pos = tagEnd;
      continue;
    }
    pos = tagEnd;
  }
  throw new Error('blockdef missing endblockdef');
};

const parseFieldDefault = (raw, fieldType) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return undefined;
  if (isMockExpression(trimmed)) return trimmed;
  if (fieldType === 'item_list' || fieldType === 'product_list') {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* ignore */
    }
  }
  if (fieldType === 'boolean') {
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
  }
  if (fieldType === 'number') {
    const n = Number(trimmed);
    if (!Number.isNaN(n)) return n;
  }
  return trimmed;
};

export const defaultItemListItemFields = () => [
  { key: 'title', type: 'text', label: 'Título' },
  { key: 'image', type: 'media', label: 'Imagem' },
];

const parseIntAttr = (attrs, key) => {
  const raw = attrs[key];
  if (raw === undefined || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const applyListFieldConstraints = (attrs, field) => {
  if (!isListFieldType(field.type)) return;
  const demo = parseIntAttr(attrs, 'demo_count');
  if (demo !== null) field.demo_count = demo;
  const min = parseIntAttr(attrs, 'min_items');
  if (min !== null) field.min_items = min;
  const max = parseIntAttr(attrs, 'max_items');
  if (max !== null) field.max_items = max;
  const minItems = field.min_items ?? 0;
  const maxItems = field.max_items ?? 0;
  if (maxItems > 0 && minItems > maxItems) {
    field.min_items = maxItems;
  }
};

const formatDefaultForFieldTag = (field) => {
  if (field.default === undefined || field.default === null || field.default === '') {
    return '';
  }
  if (field.type === 'product_list') {
    return '';
  }
  const val = typeof field.default === 'string' ? field.default : JSON.stringify(field.default);
  if (!val || val === '[]') return '';
  return val;
};

const formatDefaultAttrFragment = (field) => {
  const val = formatDefaultForFieldTag(field);
  if (!val) return '';
  if (isMockExpression(val)) return ` default=${val}`;
  return ` default="${String(val).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
};

const injectAttrsOnFieldTag = (tag, field) => {
  if (!field) return tag.trim();
  let t = tag.trim();
  if (isListFieldType(field.type)) {
    if (!/demo_count\s*=/i.test(t)) {
      t += ` demo_count="${resolveListDemoCount(field)}"`;
    }
    const min = resolveListMinItems(field);
    if (min > 0 && !/min_items\s*=/i.test(t)) {
      t += ` min_items="${min}"`;
    }
    const max = resolveListMaxItems(field);
    if (max > 0 && !/max_items\s*=/i.test(t)) {
      t += ` max_items="${max}"`;
    }
  }
  if (!/default\s*=/i.test(t)) {
    t += formatDefaultAttrFragment(field);
  }
  return t;
};

export const injectFieldDefaultAttributes = (html, blocks) => {
  if (!html || !blocks?.length) return html || '';
  const fieldsByKey = new Map();
  blocks.forEach((block) => {
    (block.fields || []).forEach((field) => {
      fieldsByKey.set(field.key, field);
    });
  });
  if (fieldsByKey.size === 0) return html;

  let out = '';
  let pos = 0;
  const lower = html.toLowerCase();
  while (pos < html.length) {
    const idx = lower.indexOf('{{ field ', pos);
    if (idx < 0) {
      out += html.slice(pos);
      break;
    }
    out += html.slice(pos, idx);
    try {
      const { tag, end } = parseTemplateTagContent(html, idx);
      const keyMatch = tag.trim().match(fieldTagPattern);
      const field = keyMatch ? fieldsByKey.get(keyMatch[1].trim()) : null;
      const needsInject =
        field &&
        ((!/default\s*=/i.test(tag) && formatDefaultForFieldTag(field)) ||
          (isListFieldType(field.type) &&
            (!/demo_count\s*=/i.test(tag) ||
              (!/min_items\s*=/i.test(tag) && resolveListMinItems(field) > 0) ||
              (!/max_items\s*=/i.test(tag) && resolveListMaxItems(field) > 0))));
      if (needsInject) {
        out += `{{ ${injectAttrsOnFieldTag(tag, field)} }}`;
      } else {
        out += html.slice(idx, end);
      }
      pos = end;
    } catch {
      out += html.slice(idx, idx + 2);
      pos = idx + 2;
    }
  }
  return out;
};

export const compileTemplateSpecForEditor = (spec) => {
  const compiled = compileTemplateSpec(spec);
  return { ...spec, html: spec?.html || '', blocks: compiled.blocks };
};

const parseFieldTag = (tag, blockId = '') => {
  const m = tag.trim().match(fieldTagPattern);
  if (!m) throw new Error(`invalid field tag: ${tag}`);
  const key = m[1].trim();
  const attrs = parseFieldTagAttributes(m[2]);
  const type = (attrs.type || '').trim();
  if (!type) throw new Error(`field "${key}" missing type`);
  if (!FIELD_TYPES.has(type)) throw new Error(`invalid field type "${type}" in field "${key}"`);
  const field = {
    key,
    type,
    label: (attrs.label || '').trim() || key,
  };
  if (attrs.default !== undefined && attrs.default !== '') {
    field.default = parseFieldDefault(attrs.default, type);
  }
  applyListFieldConstraints(attrs, field);
  return field;
};

const stripFieldTags = (body, blockId = '') => {
  const fields = [];
  let out = '';
  let pos = 0;
  while (pos < body.length) {
    const rel = body.toLowerCase().indexOf('{{ field ', pos);
    if (rel < 0) {
      out += body.slice(pos);
      break;
    }
    out += body.slice(pos, rel);
    const { tag, end } = parseTemplateTagContent(body, rel);
    fields.push(parseFieldTag(tag, blockId));
    pos = end;
  }
  return { html: out.trim(), fields };
};

const parseBlockdefOpen = (tag) => {
  const m = tag.trim().match(blockdefOpenPattern);
  if (!m) throw new Error(`invalid blockdef tag: ${tag}`);
  const id = m[1].trim();
  const attrs = parseTagAttributes(m[2]);
  return { id, label: (attrs.label || '').trim() || id };
};

export const extractBlockdefsFromHTML = (html) => {
  const blocks = [];
  let compositionHTML = '';
  let pos = 0;
  const seen = new Set();
  while (pos < html.length) {
    const abs = html.toLowerCase().indexOf('{{ blockdef ', pos);
    if (abs < 0) {
      compositionHTML += html.slice(pos);
      break;
    }
    compositionHTML += html.slice(pos, abs);
    const { tag, end: openEnd } = parseTemplateTagContent(html, abs);
    const { id, label } = parseBlockdefOpen(tag);
    if (!BLOCK_ID_PATTERN.test(id)) throw new Error(`invalid block id "${id}"`);
    if (seen.has(id)) throw new Error(`duplicate blockdef id "${id}"`);
    seen.add(id);
    const { body, closeEnd } = extractBlockdefBody(html, openEnd);
    const { html: blockHTML, fields } = stripFieldTags(body, id);
    blocks.push({ id, label, html: blockHTML, fields });
    compositionHTML += `{{ block "${id}" }}`;
    pos = closeEnd;
  }
  const trimmed = compositionHTML.trim();
  const withRefs = ensureBlockReferences(trimmed, blocks);
  return { blocks, compositionHTML: dedupeBlockReferencesKeepLast(withRefs) };
};

const validateBlockReferences = (compositionHTML, blocks) => {
  const defined = new Set(blocks.map((b) => b.id));
  let m = blockRefPattern.exec(compositionHTML);
  while (m) {
    const id = m[1].trim();
    if (!defined.has(id)) {
      throw new Error(`block "${id}" referenced in html but not defined`);
    }
    m = blockRefPattern.exec(compositionHTML);
  }
  blockRefPattern.lastIndex = 0;
};

export const compileTemplateSpec = (spec) => {
  if (!spec) throw new Error('template spec is required');
  if (!hasBlockdefs(spec.html)) {
    return { ...spec, blocks: filterBlocksByReferences(spec.blocks || [], spec.html || '') };
  }
  const sourceHTML = spec.html || '';
  const { blocks: extracted, compositionHTML: rawComposition } = extractBlockdefsFromHTML(sourceHTML);
  validateBlockReferences(rawComposition, extracted);
  const blocks = filterBlocksExcludingUnusedPreamble(sourceHTML, extracted);
  const compositionHTML = filterCompositionKeepOnlyBlocks(rawComposition, blocks);
  blocks.forEach((block) => {
    (block.fields || []).forEach((field) => {
      if (!FIELD_KEY_PATTERN.test(field.key)) {
        throw new Error(`invalid field key "${field.key}" in block "${block.id}"`);
      }
    });
  });
  return { ...spec, blocks, html: compositionHTML };
};

export const prepareTemplateSpecForSave = (spec) => {
  const compiled = compileTemplateSpec(spec);
  validateBlockReferences(
    hasBlockdefs(spec.html) ? compiled.compositionHTML : spec.html || '',
    compiled.blocks,
  );
  return { ...spec, blocks: compiled.blocks };
};

export const emitBlockdefsFromBlocks = (blocks) => {
  const escapeAttr = (s) => String(s || '').replace(/"/g, '\\"');
  const formatFieldTag = (f) => {
    let tag = `{{ field "${escapeAttr(f.key)}" type="${escapeAttr(f.type)}"`;
    if (f.label && f.label !== f.key) tag += ` label="${escapeAttr(f.label)}"`;
    if (isListFieldType(f.type) && f.demo_count != null && f.demo_count !== '') {
      tag += ` demo_count="${resolveListDemoCount(f)}"`;
      const min = resolveListMinItems(f);
      if (min > 0) tag += ` min_items="${min}"`;
      const max = resolveListMaxItems(f);
      if (max > 0) tag += ` max_items="${max}"`;
    }
    if (f.default !== undefined && f.default !== null && f.default !== '' && f.type !== 'product_list') {
      const val = typeof f.default === 'string' ? f.default : JSON.stringify(f.default);
      if (val && val !== '[]') tag += ` default="${escapeAttr(val)}"`;
    }
    return `${tag} }}`;
  };
  return (blocks || [])
    .map((b) => {
      const label = b.label || b.id;
      const lines = [`{{ blockdef "${escapeAttr(b.id)}" label="${escapeAttr(label)}" }}`];
      (b.fields || []).forEach((f) => lines.push(formatFieldTag(f)));
      lines.push((b.html || '').trim());
      lines.push('{{ endblockdef }}');
      return lines.join('\n');
    })
    .join('\n\n');
};

export const HIGHLIGHTS_BLOCKDEF_SNIPPET = `{{ blockdef "highlights" label="Destaques" }}
{{ field "title" type="text" label="Título" default="Nossos destaques" }}
{{ field "subtitle" type="textarea" label="Subtítulo" default="Conheça os principais itens do catálogo" }}
{{ field "items" type="item_list" label="Itens" demo_count="3" min_items="1" max_items="12" }}
<section class="catalog-highlights">
  <header class="catalog-highlights__header">
    <h2>{{ block.title }}</h2>
    <p>{{ block.subtitle }}</p>
  </header>
  <div class="catalog-highlights__grid">
    {{ for item in block.items demo_count="3" }}
    <article class="catalog-highlights__card">
      {{ if item.image_url }}<img src="{{ item.image_url }}" alt="{{ item.title }}" />{{ end }}
      <h3>{{ item.title }}</h3>
    </article>
    {{ end }}
  </div>
</section>
{{ endblockdef }}`;

export const PRODUCT_CARD_LOOP_BODY = `    {{ for product in __LIST__ demo_count="6" }}
    <article class="product-card">
      {{ for image in product.images }}
      <img src="{{ image.url }}" alt="{{ image.alt }}" />
      {{ end }}
      <h3>{{ product.title }}</h3>
      {{ if product.description }}<p>{{ product.description }}</p>{{ end }}
      <ul class="product-skus">
      {{ for sku in product.skus }}
        <li class="sku-row">
          <span class="sku-label">{{ sku.label }}</span>
          {{ if sku.code }}<span class="sku-code">{{ sku.code }}</span>{{ end }}
          {{ if sku.has_price }}
            <span class="sku-price">{{ sku.price }}</span>
          {{ else }}
            <span class="sku-price">Solicitar orçamento</span>
          {{ end }}
          <span class="sku-attributes">
            {{ if sku.attributes.cor }}Cor: {{ sku.attributes.cor }}{{ end }}
            {{ if sku.attributes.tamanho }} · Tam: {{ sku.attributes.tamanho }}{{ end }}
          </span>
        </li>
      {{ end }}
      </ul>
    </article>
    {{ end }}`;

const productLoopFor = (listPath) => PRODUCT_CARD_LOOP_BODY.replace('__LIST__', listPath);

export const PRODUCT_BLOCKDEF_SNIPPET = `{{ blockdef "products" label="Produtos" }}
{{ field "products" type="product_list" label="Produtos" demo_count="6" min_items="0" max_items="24" }}
<section class="catalog-products">
  <h2>Produtos</h2>
  <div class="product-grid">
${productLoopFor('block.products')}
  </div>
</section>
{{ endblockdef }}`;

export const SYSTEM_PRODUCTS_LOOP_SNIPPET = `<section class="catalog-products">
  <h2>Produtos</h2>
  <div class="product-grid">
${productLoopFor('system.products')}
  </div>
</section>`;

export const TEMPLATE_SYNTAX_REFERENCE_SNIPPET = `{{ block "hero" }}

{{ blockdef "hero" label="Hero" }}
{{ field "title" type="text" label="Título" default=mock.title() }}
<h1>{{ block.title }}</h1>
{{ endblockdef }}

{{ for product in block.products demo_count="6" }}
  <h3>{{ product.title default=mock.title() }}</h3>
  {{ for sku in product.skus }}
    {{ if sku.has_price }}{{ sku.label }} — {{ sku.price }}{{ end }}
  {{ end }}
{{ end }}

{{ if sku.has_price chance="40%" }}
  ...
{{ else }}
  ...
{{ end }}`;
